import React, { createContext, useContext, useMemo, useState } from "react";

import {
  QueryObserverResult,
  RefetchOptions,
  RefetchQueryFilters,
  useQuery,
} from "@tanstack/react-query";
import * as cryptoJS from "crypto-js";
import { useActionJobDocument } from "Hooks/useActionJobDocument";
import { useSecretManager } from "Hooks/useSecretManager";
import { CustomerPortalMilestoneStep, OrderToCustomerPortal } from "Models/customer.portal";
import { CustomerPortalShipment } from "Models/customerPortalShipment";
import { JobDocument } from "Models/job.document";
import { useParams } from "react-router-dom";
import CustomerPortalService from "Services/customer.portal.service";
import OrderService from "Services/order.service";
import { checkTokenCustomerInvite, CustomerPortalToken } from "Util/checkTokenCustomerInvite";
import { CACHE_TIME, STALE_TIME } from "Util/constants";
import env from "Util/env";
import { generateTokenBySecret } from "Util/generateTokenBySecret";
import { CustomerPortalKeys } from "Util/queries";

import { useAppState } from "./StateContext";

interface CustomerPortalJobContextProps {
  customerShipments: CustomerPortalShipment[];
  customerShipmentsWithTracking: CustomerPortalShipment[];
  order?: OrderToCustomerPortal;
  setOrder: React.Dispatch<React.SetStateAction<OrderToCustomerPortal | undefined>>;
  isLoading: boolean;
  isCustomerShipmentsLoading: boolean;
  milestonesSteps?: CustomerPortalMilestoneStep[];
  isMilestonesStepsLoading: boolean;
  isMileStonesOpenedInMobile: boolean;
  setIsMileStonesOpenedInMobile: React.Dispatch<React.SetStateAction<boolean>>;
  handleOpenMileStonesInMobile: () => void;
  handleCloseMileStonesInMobile: () => void;

  openRemoveEscalationRequest: boolean;
  setOpenRemoveEscalationRequest: (value: boolean) => void;
  openRequestEscalationModal: boolean;
  setOpenEscalationModal: (value: boolean) => void;
  escalationIsRequested: boolean;
  handleClickButtonEscalation: () => void;

  documents: JobDocument[] | undefined;
  refetchDocuments: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined
  ) => Promise<QueryObserverResult<JobDocument[] | undefined, unknown>>;
  isInitialLoadingDocuments: boolean;
  currentTab: number;
  setCurrentTab: React.Dispatch<React.SetStateAction<number>>;
}

interface CustomerPortalJobProviderProps {
  children?: React.ReactChild;
}

const CustomerPortalJobContext = createContext<CustomerPortalJobContextProps>(
  {} as CustomerPortalJobContextProps
);

const useCustomerPortalJobContext = () => {
  const context = useContext(CustomerPortalJobContext);

  return context;
};

const CustomerPortalJobProvider = (props: CustomerPortalJobProviderProps) => {
  const { children } = props;
  const { jobIdToken } = useParams<{ jobIdToken?: string }>();
  const { currentCustomerUser } = useAppState();
  const { secret } = useSecretManager(env.SECRET_ID);
  const [isMileStonesOpenedInMobile, setIsMileStonesOpenedInMobile] = useState(false);
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [order, setOrder] = useState<OrderToCustomerPortal>();
  const { getGetCustomerPortalJobDocumentsByJobId } = useActionJobDocument();

  function handleOpenMileStonesInMobile() {
    setIsMileStonesOpenedInMobile(true);
  }
  function handleCloseMileStonesInMobile() {
    setIsMileStonesOpenedInMobile(false);
  }

  const jobId = useMemo(() => {
    if (!jobIdToken || !secret?.SECRET_KEY_PASSWORD) return;

    return checkTokenCustomerInvite<CustomerPortalToken>(jobIdToken, secret.SECRET_KEY_PASSWORD)
      ?.jobId;
  }, [jobIdToken, secret]);

  const { isFetching: isLoading } = useQuery({
    queryKey: ["orders.get.customer.portal", jobId],
    queryFn: async () => {
      const valueToEncrypt = {
        jobId,
        companyId: currentCustomerUser?.companyId,
        customerId: currentCustomerUser?.customerId,
      };

      const token = generateTokenBySecret(valueToEncrypt, secret);

      const response = await OrderService.getCustomerPortal(token);

      if (response?.data) {
        setOrder(response?.data);

        return response?.data;
      }

      return undefined;
    },
    enabled: !!(secret?.SECRET_KEY_PASSWORD && jobIdToken),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  const getCustomerShipments = () => {
    return useQuery(
      ["shipment.list.customer.portal", secret?.SECRET_KEY_PASSWORD, order?.id],
      async () => {
        const valueToEncrypt = { jobId: order?.id };

        const token = generateTokenBySecret(valueToEncrypt, secret);

        const response = await CustomerPortalService.getShipments(token);

        if (response?.data) return response.data;

        return [] as CustomerPortalShipment[];
      },
      {
        enabled: !!secret?.SECRET_KEY_PASSWORD && !!order?.id,
        refetchOnWindowFocus: false,
      }
    );
  };
  const { data: customerShipments = [], isLoading: isCustomerShipmentsLoading } =
    getCustomerShipments();

  const customerShipmentsWithTracking = useMemo(() => {
    return customerShipments.filter((shipment) => !!shipment.shipmentTracking?.vesselPosition);
  }, [customerShipments]);

  const getMilestonesSteps = () => {
    return useQuery({
      queryKey: CustomerPortalKeys.getMilestones([currentCustomerUser?.companyId, order?.id]),
      queryFn: async () => {
        const valueToEncrypt = { jobId: order?.id };

        const jobIdEncripted = secret?.SECRET_KEY_PASSWORD
          ? cryptoJS.AES.encrypt(
              JSON.stringify(valueToEncrypt),
              secret.SECRET_KEY_PASSWORD
            ).toString()
          : "";

        const response = await CustomerPortalService.getMilestoneSteps(jobIdEncripted);

        if (response && response?.data) return response.data;

        return [] as CustomerPortalMilestoneStep[];
      },
      enabled: !!currentCustomerUser?.companyId && !!order?.id,
      staleTime: STALE_TIME,
      cacheTime: CACHE_TIME,
    });
  };

  const { data: milestonesSteps, isFetching: isMilestonesStepsLoading } = getMilestonesSteps();

  const {
    data: documents,
    refetch: refetchDocuments,
    isInitialLoading: isInitialLoadingDocuments,
  } = getGetCustomerPortalJobDocumentsByJobId(order?.id);

  const [openRemoveEscalationRequest, setOpenRemoveEscalationRequest] = useState<boolean>(false);
  const [openRequestEscalationModal, setOpenEscalationModal] = useState<boolean>(false);

  const handleClickButtonEscalation = () => {
    // TODO commented this while we doon't work with the escalation request
    // if (escalationIsRequested) {
    //   setOpenRemoveEscalationRequest(true);

    //   return;
    // }

    setOpenEscalationModal(true);
  };

  const escalationIsRequested = useMemo(() => {
    // TODO commented this while we doon't work with the escalation request
    // if (order?.requestEscalation?.isRequested) return true;

    return false;
  }, [order?.requestEscalation]);

  return (
    <CustomerPortalJobContext.Provider
      value={{
        order,
        setOrder,
        isLoading: !order || isLoading,
        customerShipments,
        customerShipmentsWithTracking,
        isCustomerShipmentsLoading,
        milestonesSteps,
        isMilestonesStepsLoading,
        isMileStonesOpenedInMobile,
        setIsMileStonesOpenedInMobile,
        handleOpenMileStonesInMobile,
        handleCloseMileStonesInMobile,

        openRemoveEscalationRequest,
        setOpenRemoveEscalationRequest,
        openRequestEscalationModal,
        setOpenEscalationModal,
        escalationIsRequested,
        handleClickButtonEscalation,

        documents,
        refetchDocuments,
        isInitialLoadingDocuments,
        currentTab,
        setCurrentTab,
      }}
    >
      {children}
    </CustomerPortalJobContext.Provider>
  );
};

export { CustomerPortalJobContext, CustomerPortalJobProvider, useCustomerPortalJobContext };
